import { useState } from "react";
import { accommodations } from "@/mocks/accommodations";

interface AirbnbLink {
  id: number;
  name: string;
  area: string;
  url: string;
  status: "linked" | "pending";
  isCustom?: boolean;
}

const STORAGE_KEY = "admin_airbnb_links";

const getInitialLinks = (): AirbnbLink[] => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) return JSON.parse(saved);
  } catch {
    // ignore
  }
  return accommodations.map((acc) => ({
    id: acc.id,
    name: acc.name,
    area: acc.area,
    url: acc.airbnbUrl || "",
    status: acc.airbnbUrl ? "linked" : "pending",
    isCustom: false,
  }));
};

const AREAS = ["텐진", "하카타", "나카스", "오호리", "모모치", "야쿠인", "기온", "캐널시티", "스미요시", "마이즈루", "니시진", "시사이드모모치", "기타"];

export default function AirbnbSettingsTab() {
  const [links, setLinks] = useState<AirbnbLink[]>(getInitialLinks);
  const [editId, setEditId] = useState<number | null>(null);
  const [editUrl, setEditUrl] = useState("");
  const [toast, setToast] = useState<{ msg: string; type: "success" | "error" } | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState<"all" | "linked" | "pending">("all");
  const [showAddForm, setShowAddForm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<number | null>(null);
  const [newForm, setNewForm] = useState({ name: "", area: "텐진", url: "" });

  const persist = (updated: AirbnbLink[]) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  };

  const showToast = (msg: string, type: "success" | "error" = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2500);
  };

  const startEdit = (link: AirbnbLink) => {
    setEditId(link.id);
    setEditUrl(link.url);
  };

  const saveEdit = (id: number) => {
    const trimmed = editUrl.trim();
    const updated = links.map((l) =>
      l.id === id ? { ...l, url: trimmed, status: (trimmed ? "linked" : "pending") as "linked" | "pending" } : l
    );
    setLinks(updated);
    persist(updated);
    setEditId(null);
    setEditUrl("");
    showToast("에어비앤비 링크가 저장됐어요!");
  };

  const clearUrl = (id: number) => {
    const updated = links.map((l) => (l.id === id ? { ...l, url: "", status: "pending" as const } : l));
    setLinks(updated);
    persist(updated);
    showToast("링크가 제거됐어요.");
  };

  const deleteAccommodation = (id: number) => {
    const updated = links.filter((l) => l.id !== id);
    setLinks(updated);
    persist(updated);
    setShowDeleteConfirm(null);
    showToast("숙소가 삭제됐어요.");
  };

  const addAccommodation = () => {
    if (!newForm.name.trim()) {
      showToast("숙소명을 입력해주세요.", "error");
      return;
    }
    const newId = Math.max(...links.map((l) => l.id), 0) + 1;
    const newLink: AirbnbLink = {
      id: newId,
      name: newForm.name.trim(),
      area: newForm.area,
      url: newForm.url.trim(),
      status: newForm.url.trim() ? "linked" : "pending",
      isCustom: true,
    };
    const updated = [...links, newLink];
    setLinks(updated);
    persist(updated);
    setNewForm({ name: "", area: "텐진", url: "" });
    setShowAddForm(false);
    showToast(`"${newLink.name}" 숙소가 추가됐어요!`);
  };

  const filtered = links.filter((l) => {
    const matchSearch = l.name.includes(searchQuery) || l.area.includes(searchQuery);
    const matchStatus = filterStatus === "all" || l.status === filterStatus;
    return matchSearch && matchStatus;
  });

  const linkedCount = links.filter((l) => l.status === "linked").length;
  const pendingCount = links.filter((l) => l.status === "pending").length;

  return (
    <div className="max-w-4xl">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-6 right-6 z-50 text-white text-sm px-4 py-3 rounded-xl transition-all ${toast.type === "error" ? "bg-red-500" : "bg-stone-800"}`}>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className={toast.type === "error" ? "ri-error-warning-line" : "ri-check-line"}></i>
            </div>
            {toast.msg}
          </div>
        </div>
      )}

      {/* 삭제 확인 모달 */}
      {showDeleteConfirm !== null && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-80 border border-stone-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-red-50">
                <i className="ri-delete-bin-line text-red-500 text-base"></i>
              </div>
              <h3 className="text-sm font-bold text-stone-800">숙소 삭제</h3>
            </div>
            <p className="text-sm text-stone-500 mb-5">
              <strong className="text-stone-700">{links.find((l) => l.id === showDeleteConfirm)?.name}</strong> 숙소를 목록에서 삭제할까요?<br />
              <span className="text-xs text-stone-400 mt-1 block">삭제 후 다시 추가하려면 직접 입력해야 해요.</span>
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="flex-1 py-2 border border-stone-200 text-stone-500 text-sm rounded-lg cursor-pointer whitespace-nowrap hover:bg-stone-50"
              >
                취소
              </button>
              <button
                onClick={() => deleteAccommodation(showDeleteConfirm)}
                className="flex-1 py-2 bg-red-500 text-white text-sm rounded-lg cursor-pointer whitespace-nowrap hover:bg-red-600"
              >
                삭제
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 헤더 */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-stone-800">에어비앤비 연동</h2>
          <p className="text-sm text-stone-400 mt-1">각 숙소의 에어비앤비 예약 링크를 관리하세요.</p>
        </div>
        <button
          onClick={() => { setShowAddForm(true); setEditId(null); }}
          className="flex items-center gap-2 px-4 py-2.5 bg-stone-800 text-white text-sm rounded-xl cursor-pointer whitespace-nowrap hover:bg-stone-700 transition-colors"
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-add-line text-base"></i>
          </div>
          숙소 추가
        </button>
      </div>

      {/* 숙소 추가 폼 */}
      {showAddForm && (
        <div className="bg-white rounded-2xl border-2 border-stone-800 border-dashed p-5 mb-6">
          <h3 className="text-sm font-bold text-stone-800 mb-4 flex items-center gap-2">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-add-circle-line text-stone-600"></i>
            </div>
            새 숙소 추가
          </h3>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-xs font-medium text-stone-600 mb-1.5">숙소명 <span className="text-red-400">*</span></label>
              <input
                type="text"
                className="w-full px-3 py-2.5 border border-stone-200 rounded-lg text-sm focus:outline-none focus:border-stone-500 text-stone-700 bg-stone-50"
                placeholder="예) 텐진 신규 스튜디오"
                value={newForm.name}
                onChange={(e) => setNewForm((p) => ({ ...p, name: e.target.value }))}
                autoFocus
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-stone-600 mb-1.5">지역</label>
              <select
                className="w-full px-3 py-2.5 border border-stone-200 rounded-lg text-sm focus:outline-none focus:border-stone-500 text-stone-700 bg-stone-50 cursor-pointer"
                value={newForm.area}
                onChange={(e) => setNewForm((p) => ({ ...p, area: e.target.value }))}
              >
                {AREAS.map((a) => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="mb-4">
            <label className="block text-xs font-medium text-stone-600 mb-1.5">에어비앤비 URL <span className="text-stone-400">(선택)</span></label>
            <input
              type="text"
              className="w-full px-3 py-2.5 border border-stone-200 rounded-lg text-sm focus:outline-none focus:border-stone-500 text-stone-700 bg-stone-50"
              placeholder="https://www.airbnb.co.kr/rooms/..."
              value={newForm.url}
              onChange={(e) => setNewForm((p) => ({ ...p, url: e.target.value }))}
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={addAccommodation}
              className="px-5 py-2 bg-stone-800 text-white text-sm rounded-lg cursor-pointer whitespace-nowrap hover:bg-stone-700"
            >
              추가하기
            </button>
            <button
              onClick={() => { setShowAddForm(false); setNewForm({ name: "", area: "텐진", url: "" }); }}
              className="px-5 py-2 border border-stone-200 text-stone-500 text-sm rounded-lg cursor-pointer whitespace-nowrap hover:bg-stone-50"
            >
              취소
            </button>
          </div>
        </div>
      )}

      {/* 통계 */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        {[
          { label: "전체 숙소", value: links.length, icon: "ri-home-2-line", color: "text-stone-600", filter: "all" as const },
          { label: "연동 완료", value: linkedCount, icon: "ri-links-line", color: "text-emerald-600", filter: "linked" as const },
          { label: "링크 미설정", value: pendingCount, icon: "ri-link-unlink", color: "text-amber-600", filter: "pending" as const },
        ].map((stat) => (
          <button
            key={stat.label}
            onClick={() => setFilterStatus(filterStatus === stat.filter ? "all" : stat.filter)}
            className={`bg-white rounded-xl border p-4 flex items-center gap-3 cursor-pointer transition-all text-left ${filterStatus === stat.filter ? "border-stone-800 ring-2 ring-stone-800/10" : "border-stone-100 hover:border-stone-300"}`}
          >
            <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-stone-50 flex-shrink-0">
              <i className={`${stat.icon} ${stat.color} text-base`}></i>
            </div>
            <div>
              <p className="text-2xl font-bold text-stone-800">{stat.value}</p>
              <p className="text-xs text-stone-400">{stat.label}</p>
            </div>
          </button>
        ))}
      </div>

      {/* 검색 */}
      <div className="relative mb-5">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
          <i className="ri-search-line text-stone-400 text-sm"></i>
        </div>
        <input
          type="text"
          className="w-full pl-9 pr-4 py-2.5 border border-stone-200 rounded-lg text-sm focus:outline-none focus:border-stone-400 text-stone-700 bg-white"
          placeholder="숙소명 또는 지역으로 검색..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-stone-400 hover:text-stone-600 cursor-pointer"
          >
            <i className="ri-close-line text-sm"></i>
          </button>
        )}
      </div>

      {/* 가이드 박스 */}
      <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 mb-5">
        <p className="text-xs font-semibold text-amber-700 mb-1">
          <i className="ri-lightbulb-line mr-1"></i>에어비앤비 링크 연동 방법
        </p>
        <ol className="text-xs text-amber-600 space-y-0.5 list-decimal list-inside">
          <li>에어비앤비 앱 또는 웹사이트에서 해당 숙소 페이지를 열어주세요.</li>
          <li>브라우저 주소창의 URL 전체를 복사하세요. (예: https://www.airbnb.co.kr/rooms/12345678)</li>
          <li>아래 해당 숙소의 수정 버튼을 눌러 URL을 붙여넣고 저장하세요.</li>
        </ol>
      </div>

      {/* 숙소 목록 */}
      <div className="space-y-3">
        {filtered.length === 0 ? (
          <div className="bg-white rounded-xl border border-stone-100 p-10 text-center">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-stone-50 mx-auto mb-3">
              <i className="ri-search-line text-stone-400 text-xl"></i>
            </div>
            <p className="text-sm text-stone-400">검색 결과가 없어요.</p>
          </div>
        ) : (
          filtered.map((link) => (
            <div key={link.id} className="bg-white rounded-xl border border-stone-100 p-4">
              <div className="flex items-start gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-sm font-semibold text-stone-800">{link.name}</span>
                    <span className="px-2 py-0.5 bg-stone-100 text-stone-500 text-xs rounded-full">{link.area}</span>
                    {link.isCustom && (
                      <span className="px-2 py-0.5 bg-stone-800 text-white text-xs rounded-full">신규</span>
                    )}
                    {link.status === "linked" ? (
                      <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded-full font-medium">
                        <i className="ri-links-line mr-0.5"></i>연동 완료
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 bg-amber-100 text-amber-600 text-xs rounded-full font-medium">
                        <i className="ri-error-warning-line mr-0.5"></i>미설정
                      </span>
                    )}
                  </div>

                  {editId === link.id ? (
                    <div className="mt-2">
                      <input
                        type="text"
                        className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:border-stone-500 text-stone-700"
                        placeholder="https://www.airbnb.co.kr/rooms/..."
                        value={editUrl}
                        onChange={(e) => setEditUrl(e.target.value)}
                        autoFocus
                      />
                      <div className="flex gap-2 mt-2">
                        <button
                          onClick={() => saveEdit(link.id)}
                          className="px-4 py-1.5 bg-stone-800 text-white text-xs rounded-md cursor-pointer whitespace-nowrap hover:bg-stone-700"
                        >
                          저장
                        </button>
                        <button
                          onClick={() => { setEditId(null); setEditUrl(""); }}
                          className="px-4 py-1.5 border border-stone-200 text-stone-500 text-xs rounded-md cursor-pointer whitespace-nowrap hover:bg-stone-50"
                        >
                          취소
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-1.5">
                      {link.url ? (
                        <a
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer nofollow"
                          className="text-xs text-stone-400 hover:text-stone-600 underline truncate block max-w-sm"
                        >
                          {link.url}
                        </a>
                      ) : (
                        <p className="text-xs text-stone-300">링크가 설정되지 않았어요.</p>
                      )}
                    </div>
                  )}
                </div>

                {editId !== link.id && (
                  <div className="flex gap-1.5 flex-shrink-0">
                    <button
                      onClick={() => startEdit(link)}
                      className="px-3 py-1.5 border border-stone-200 text-stone-600 text-xs rounded-md cursor-pointer whitespace-nowrap hover:bg-stone-50"
                    >
                      <i className="ri-edit-line mr-1"></i>수정
                    </button>
                    {link.url && (
                      <>
                        <a
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer nofollow"
                          className="px-3 py-1.5 bg-orange-500 text-white text-xs rounded-md cursor-pointer whitespace-nowrap hover:bg-orange-600"
                        >
                          <i className="ri-external-link-line mr-1"></i>바로가기
                        </a>
                        <button
                          onClick={() => clearUrl(link.id)}
                          className="px-3 py-1.5 border border-amber-100 text-amber-500 text-xs rounded-md cursor-pointer whitespace-nowrap hover:bg-amber-50"
                          title="URL만 제거"
                        >
                          <i className="ri-link-unlink"></i>
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => setShowDeleteConfirm(link.id)}
                      className="px-3 py-1.5 border border-red-100 text-red-400 text-xs rounded-md cursor-pointer whitespace-nowrap hover:bg-red-50"
                      title="숙소 삭제"
                    >
                      <i className="ri-delete-bin-line"></i>
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {filtered.length > 0 && (
        <p className="text-xs text-stone-400 text-center mt-4">
          총 {links.length}개 숙소 중 {filtered.length}개 표시 중
        </p>
      )}
    </div>
  );
}
